import { useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  datoIOslo,
  erBilde,
  filstorrelse,
  fremdrift,
  MAKS_VEDLEGG,
  PRIORITETER,
  PRIORITETSNAVN,
  REAKSJONER,
  SAKSTYPENAVN,
  tidstekst,
  type InnleggDto,
  type Prioritet,
  type SakDto,
  type Sakstype,
  type VedleggDto,
} from '@es/shared';
import { Knapp } from '../komponenter/Knapp.js';
import { Feilmelding, Laster } from '../komponenter/Tilstand.js';
import { krympBilde } from './krymp.js';
import {
  aktivOmtale,
  avatarfarge,
  datotekst,
  forbokstaver,
  forslag,
  handlingstekst,
  lesEtiketter,
  lesVarighet,
  omtalteIder,
  settInnOmtale,
  tidspunkt,
} from './sak.js';
import {
  lastNed,
  useEndreDeloppgave,
  useForTid,
  useLagreDetaljer,
  useLastOppVedlegg,
  useNyDeloppgave,
  useNyttInnlegg,
  useSak,
  useSlettDeloppgave,
  useSlettInnlegg,
  useSlettTid,
  useSlettVedlegg,
  useVedleggsbilde,
  useVeksleReaksjon,
  type Sakreferanse,
} from './sporringer.js';

const FELT = 'min-h-11 rounded-md border border-kant-sterk bg-flate px-3 text-sm text-tekst';
const IKONKNAPP =
  'inline-flex size-9 items-center justify-center rounded-md text-tekst-dempet hover:bg-flate-senket hover:text-tekst focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-merke disabled:opacity-50';

const TONE: Record<SakDto['status']['tone'], string> = {
  noytral: 'bg-flate-senket text-tekst',
  info: 'bg-merke-svak text-merke-sterk',
  varsel: 'bg-varsel-svak text-tekst',
  positiv: 'bg-positiv-svak text-positiv',
  negativ: 'bg-negativ-svak text-negativ',
};
const PRIORITETSTONE: Record<Prioritet, string> = {
  lav: 'bg-flate-senket text-tekst',
  middels: 'bg-merke-svak text-merke-sterk',
  hoy: 'bg-varsel-svak text-tekst',
  kritisk: 'bg-negativ-svak text-negativ',
};
const MERKE = 'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium';

/** Lenketeksten til listen saken hører til. */
const HORER_TIL: Record<Sakstype, string> = {
  avvik: 'Avvikene',
  kontroll: 'Kontrollene på bygget',
  aksjonspunkt: 'Aksjonspunktene',
  tiltak: 'Tiltakene',
  arbeidsordre: 'Arbeidsordrene',
};

/**
 * Én sak: et avvik, en kontroll, et aksjonspunkt, et tiltak. Sammendraget og
 * diskusjonen til venstre; detaljene, deloppgavene, vedleggene og tiden til
 * høyre. Hva saken er og hvem som kan hva, kommer fra modulen den hører til.
 */
export function Saksside({
  type,
  id,
  tilbake,
  tillegg,
}: {
  readonly type: Sakstype;
  readonly id: string;
  /** Lenken tilbake til listen saken hører til. */
  readonly tilbake?: { readonly til: string; readonly tekst: string };
  /** Det modulen viser i tillegg, under sammendraget: arbeidsordrens status og kostnader. */
  readonly tillegg?: ReactNode;
}): ReactElement {
  const sak = useSak(type, id);
  if (sak.isPending) return <Laster hva="saken" />;
  if (sak.isError) return <Feilmelding feil={sak.error} />;
  const s = sak.data;
  const ref: Sakreferanse = { type, id };

  return (
    <div className="flex flex-col gap-4">
      {tilbake ? (
        <Link to={tilbake.til} className="text-sm text-merke underline">
          {tilbake.tekst}
        </Link>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Sammendrag s={s} />
          {tillegg}
          <Diskusjon s={s} sak={ref} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Detaljer s={s} sak={ref} />
          <Deloppgaver s={s} sak={ref} />
          <Vedlegg s={s} sak={ref} />
          <Tid s={s} sak={ref} />
        </div>
      </div>
    </div>
  );
}

export function Seksjon({
  tittel,
  handling,
  children,
  flat = false,
}: {
  readonly tittel: string;
  readonly handling?: ReactNode;
  readonly children: ReactNode;
  readonly flat?: boolean;
}): ReactElement {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-lg border border-kant bg-flate shadow-kort">
      {/* Samme hode som kortene ellers i løsningen, med streken fra designet. */}
      <div className="flex min-h-14 items-center justify-between gap-2 rounded-t-lg border-b border-kant bg-kort-hode px-4">
        <h2 id={id} className="border-l-[3px] border-merke pl-2 text-base font-semibold text-tekst">
          {tittel}
        </h2>
        {handling}
      </div>
      <div className={flat ? '' : 'p-4'}>{children}</div>
    </section>
  );
}

function Avatar({
  person,
  liten = false,
}: {
  readonly person: { readonly id: string; readonly navn: string } | null;
  readonly liten?: boolean;
}): ReactElement {
  const storrelse = liten ? 'size-7 text-[10px]' : 'size-9 text-xs';
  if (person === null) {
    return (
      <span
        aria-hidden="true"
        className={`${storrelse} inline-flex shrink-0 items-center justify-center rounded-full bg-flate-senket text-tekst-dempet`}
      >
        ●
      </span>
    );
  }
  return (
    <span
      title={person.navn}
      aria-hidden="true"
      className={`${storrelse} inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-2 ring-flate`}
      style={{ backgroundColor: `var(--serie-${String(avatarfarge(person.id))})` }}
    >
      {forbokstaver(person.navn)}
    </span>
  );
}

// --- Sammendraget --------------------------------------------------------------

function Sammendrag({ s }: { readonly s: SakDto }): ReactElement {
  const prosent = fremdrift(s.deloppgaver);
  return (
    <Seksjon tittel="Sammendrag" flat>
      <div className="p-4">
        <p className="text-xs font-medium text-tekst-dempet">
          {SAKSTYPENAVN[s.sakstype]} {s.nummer}
        </p>
        <h1 className="mt-1 flex items-start gap-2 text-lg font-semibold text-tekst">
          <span aria-hidden="true" className="mt-2.5 size-2 shrink-0 rounded-full bg-merke" />
          {s.tittel}
        </h1>
        {s.beskrivelse === '' ? null : (
          <>
            <h2 className="mt-4 text-sm font-semibold text-tekst">Beskrivelse</h2>
            <p className="mt-1 whitespace-pre-line text-sm text-tekst-dempet">{s.beskrivelse}</p>
          </>
        )}
        {s.deloppgaver.length > 0 ? (
          <>
            <h2 className="mt-4 text-sm font-semibold text-tekst">Det som skal gjøres</h2>
            <ol className="mt-1 list-decimal pl-5 text-sm text-tekst-dempet">
              {s.deloppgaver.map((d) => (
                <li key={d.id} className={d.ferdig ? 'line-through' : ''}>
                  {d.tekst}
                </li>
              ))}
            </ol>
          </>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-4 border-t border-kant p-4 sm:grid-cols-3 lg:grid-cols-5">
        {s.felt.map((f) => (
          <div key={f.navn} className="min-w-0">
            <dt className="text-xs text-tekst-dempet">{f.navn}</dt>
            <dd className="mt-0.5 break-words text-sm font-medium text-tekst">{f.verdi}</dd>
          </div>
        ))}
        <div>
          <dt className="text-xs text-tekst-dempet">Fremdrift</dt>
          <dd className="mt-1 flex items-center gap-2 text-xs text-tekst-dempet">
            {prosent === null ? (
              'Ingen deloppgaver'
            ) : (
              <>
                <span
                  role="progressbar"
                  aria-valuenow={prosent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Fremdrift"
                  className="h-1.5 w-20 overflow-hidden rounded-full bg-flate-senket"
                >
                  <span
                    className="block h-full bg-merke"
                    style={{ width: `${String(prosent)}%` }}
                  />
                </span>
                {prosent} %
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-tekst-dempet">Innsats</dt>
          <dd className="mt-0.5 text-sm font-medium text-tekst">
            {s.tid.totalMinutter === 0 ? '–' : tidstekst(s.tid.totalMinutter)}
          </dd>
        </div>
      </dl>
    </Seksjon>
  );
}

// --- Diskusjonen ---------------------------------------------------------------

function Diskusjon({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const vedlegg = new Map(s.vedlegg.map((v) => [v.id, v]));
  return (
    <Seksjon tittel="Diskusjon" flat>
      {s.innlegg.length === 0 ? (
        <p className="p-4 text-sm text-tekst-dempet">Ingen har skrevet noe ennå.</p>
      ) : (
        <ol className="relative p-4">
          {s.innlegg.map((i, n) => (
            <Innlegg
              key={i.id}
              i={i}
              vedlegg={i.vedlegg.flatMap((v) => vedlegg.get(v) ?? [])}
              sist={n === s.innlegg.length - 1}
            />
          ))}
        </ol>
      )}
      {s.kanBidra ? <Skrivefelt s={s} sak={sak} /> : null}
    </Seksjon>
  );
}

function Innlegg({
  i,
  vedlegg,
  sist,
}: {
  readonly i: InnleggDto;
  readonly vedlegg: readonly VedleggDto[];
  readonly sist: boolean;
}): ReactElement {
  const reager = useVeksleReaksjon();
  const slett = useSlettInnlegg();
  const [velger, settVelger] = useState(false);
  const bilder = vedlegg.filter((v) => erBilde(v.mimetype));
  const filer = vedlegg.filter((v) => !erBilde(v.mimetype));
  const navn = i.forfatter?.navn ?? 'Systemet';
  return (
    <li className="relative flex gap-3 pb-6">
      {sist ? null : (
        <span
          aria-hidden="true"
          className="absolute top-10 bottom-0 left-[17px] w-px border-l border-dashed border-kant"
        />
      )}
      <Avatar person={i.forfatter} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <p className="text-sm text-tekst">
            <span className="font-semibold">{navn}</span> {handlingstekst(i, vedlegg.length)}
          </p>
          <time dateTime={i.tidspunkt} className="text-xs text-tekst-dempet">
            {tidspunkt(i.tidspunkt)}
          </time>
        </div>
        {i.tekst === '' ? null : (
          <p
            className={`mt-1 whitespace-pre-line text-sm ${i.type === 'hendelse' ? 'text-tekst-dempet italic' : 'text-tekst-dempet'}`}
          >
            {i.tekst}
          </p>
        )}
        {bilder.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-2">
            {bilder.map((b) => (
              <li key={b.id}>
                <Miniatyr v={b} />
              </li>
            ))}
          </ul>
        ) : null}
        {filer.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-2">
            {filer.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => void lastNed(f.id, f.filnavn)}
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-kant px-3 text-xs text-tekst hover:bg-flate-dempet"
                >
                  <Filikon mimetype={f.mimetype} />
                  <span className="max-w-48 truncate">{f.filnavn}</span>
                  <span className="text-tekst-dempet">{filstorrelse(f.storrelse)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {i.type === 'kommentar' ? (
          <div className="mt-2 flex flex-wrap items-center gap-1">
            {i.reaksjoner.map((r) => (
              <button
                key={r.emoji}
                type="button"
                aria-pressed={r.minEgen}
                aria-label={`${r.emoji} ${String(r.antall)}${r.minEgen ? ', din reaksjon' : ''}`}
                onClick={() => {
                  reager.mutate({ innleggId: i.id, emoji: r.emoji });
                }}
                className={`inline-flex min-h-8 items-center gap-1 rounded-full border px-2 text-xs ${
                  r.minEgen
                    ? 'border-merke bg-merke-svak text-merke-sterk'
                    : 'border-kant text-tekst'
                }`}
              >
                <span aria-hidden="true">{r.emoji}</span>
                {r.antall}
              </button>
            ))}
            <button
              type="button"
              aria-expanded={velger}
              onClick={() => {
                settVelger(!velger);
              }}
              className="inline-flex min-h-8 items-center rounded-full border border-dashed border-kant px-2 text-xs text-tekst-dempet hover:text-tekst"
            >
              <span aria-hidden="true">☺+</span>
              <span className="sr-only">Reager på innlegget fra {navn}</span>
            </button>
            {velger
              ? REAKSJONER.map((e) => (
                  <button
                    key={e}
                    type="button"
                    aria-label={`Reager med ${e}`}
                    onClick={() => {
                      reager.mutate({ innleggId: i.id, emoji: e });
                      settVelger(false);
                    }}
                    className="inline-flex size-8 items-center justify-center rounded-full hover:bg-flate-senket"
                  >
                    {e}
                  </button>
                ))
              : null}
            {i.kanSlette ? (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Slette innlegget, med vedleggene i det?')) slett.mutate(i.id);
                }}
                className="ml-auto min-h-8 rounded px-2 text-xs text-tekst-dempet underline hover:text-negativ"
              >
                Slett<span className="sr-only"> innlegget fra {navn}</span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function Miniatyr({ v }: { readonly v: VedleggDto }): ReactElement {
  const bilde = useVedleggsbilde(v.id);
  return (
    <button
      type="button"
      onClick={() => void lastNed(v.id, v.filnavn)}
      title={`Last ned ${v.filnavn}`}
      className="block size-20 overflow-hidden rounded-md border border-kant bg-flate-senket focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-merke"
    >
      {bilde.data === undefined ? (
        <span className="sr-only">{v.filnavn}</span>
      ) : (
        <img src={bilde.data} alt={v.filnavn} className="size-full object-cover" />
      )}
    </button>
  );
}

function Filikon({ mimetype }: { readonly mimetype: string }): ReactElement {
  const merke = erBilde(mimetype)
    ? 'BILDE'
    : mimetype === 'application/pdf'
      ? 'PDF'
      : mimetype.includes('word')
        ? 'DOC'
        : mimetype.includes('sheet')
          ? 'XLS'
          : mimetype.includes('presentation')
            ? 'PPT'
            : 'TXT';
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-8 w-7 shrink-0 items-end justify-center rounded-sm bg-merke-svak pb-0.5 text-[8px] font-bold text-merke-sterk"
    >
      {merke}
    </span>
  );
}

/** Skrivefeltet nederst: tekst med @omtale, emoji, vedlegg og kamera. */
function Skrivefelt({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const id = useId();
  const [tekst, settTekst] = useState('');
  const [markor, settMarkor] = useState(0);
  const [ventende, settVentende] = useState<VedleggDto[]>([]);
  const [feil, settFeil] = useState<string | null>(null);
  const [emoji, settEmoji] = useState(false);
  const felt = useRef<HTMLTextAreaElement>(null);
  const fil = useRef<HTMLInputElement>(null);
  const kamera = useRef<HTMLInputElement>(null);
  const post = useNyttInnlegg(sak);
  const lastOpp = useLastOppVedlegg(sak);
  const omtale = aktivOmtale(tekst, markor);
  const forslagene = omtale === null ? [] : forslag(s.personer, omtale.sok);

  const settInn = (t: string, ny: number) => {
    settTekst(t);
    settMarkor(ny);
    window.requestAnimationFrame(() => {
      felt.current?.focus();
      felt.current?.setSelectionRange(ny, ny);
    });
  };

  const velgFiler = async (filer: FileList | null) => {
    if (filer === null) return;
    settFeil(null);
    for (const f of Array.from(filer)) {
      const klar = await krympBilde(f).catch(() => f);
      if (klar.size > MAKS_VEDLEGG) {
        settFeil(`${f.name} er større enn 10 MB.`);
        continue;
      }
      try {
        const { id: vedleggId } = await lastOpp.mutateAsync(klar);
        settVentende((v) => [
          ...v,
          {
            id: vedleggId,
            filnavn: klar.name,
            mimetype: klar.type,
            storrelse: klar.size,
            lastetOpp: { tidspunkt: new Date().toISOString(), av: null },
            innleggId: null,
            kanSlette: true,
          },
        ]);
      } catch {
        settFeil(`${f.name} kunne ikke legges ved.`);
      }
    }
  };

  return (
    <form
      aria-label="Skriv i diskusjonen"
      className="border-t border-kant p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (tekst.trim() === '' && ventende.length === 0) return;
        post.mutate(
          {
            tekst: tekst.trim(),
            omtalte: omtalteIder(tekst, s.personer),
            vedlegg: ventende.map((v) => v.id),
          },
          {
            onSuccess: () => {
              settTekst('');
              settVentende([]);
            },
          },
        );
      }}
    >
      <div className="relative">
        <label htmlFor={`${id}-tekst`} className="sr-only">
          Skriv noe. Bruk @ for å nevne en kollega.
        </label>
        <textarea
          id={`${id}-tekst`}
          ref={felt}
          value={tekst}
          rows={2}
          maxLength={4000}
          placeholder="Skriv noe … Bruk @ for å nevne en kollega."
          onChange={(e) => {
            settTekst(e.target.value);
            settMarkor(e.target.selectionStart);
          }}
          onSelect={(e) => {
            settMarkor(e.currentTarget.selectionStart);
          }}
          className={`${FELT} w-full py-2`}
        />
        {omtale !== null && forslagene.length > 0 ? (
          <ul
            aria-label="Nevn en kollega"
            className="absolute bottom-full left-0 z-10 mb-1 w-64 rounded-md border border-kant bg-flate py-1 shadow-lg"
          >
            {forslagene.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => {
                    const ny = settInnOmtale(tekst, omtale, p.navn);
                    settInn(ny.tekst, ny.markor);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm hover:bg-flate-dempet"
                >
                  <Avatar person={p} liten />
                  {p.navn}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {ventende.length > 0 ? (
        <ul aria-label="Legges ved" className="mt-2 flex flex-wrap gap-2">
          {ventende.map((v) => (
            <li
              key={v.id}
              className="inline-flex items-center gap-2 rounded-md bg-flate-senket px-2 py-1 text-xs text-tekst"
            >
              <Filikon mimetype={v.mimetype} />
              {v.filnavn}
            </li>
          ))}
        </ul>
      ) : null}
      {feil ? (
        <p role="alert" className="mt-2 text-sm text-negativ">
          {feil}
        </p>
      ) : null}
      {post.isError ? <Feilmelding feil={post.error} /> : null}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <button
          type="button"
          aria-expanded={emoji}
          onClick={() => {
            settEmoji(!emoji);
          }}
          className={IKONKNAPP}
          title="Emoji"
        >
          <span aria-hidden="true">☺</span>
          <span className="sr-only">Sett inn emoji</span>
        </button>
        {emoji
          ? REAKSJONER.map((e) => (
              <button
                key={e}
                type="button"
                aria-label={`Sett inn ${e}`}
                onClick={() => {
                  settInn(`${tekst.slice(0, markor)}${e}${tekst.slice(markor)}`, markor + e.length);
                  settEmoji(false);
                }}
                className={IKONKNAPP}
              >
                {e}
              </button>
            ))
          : null}
        <button
          type="button"
          onClick={() => fil.current?.click()}
          disabled={lastOpp.isPending}
          className={IKONKNAPP}
          title="Legg ved fil"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4" fill="currentColor">
            <path d="M13.2 3.3a3 3 0 0 1 4.2 4.3l-7.4 7.3a4.5 4.5 0 0 1-6.4-6.3l6-6a1 1 0 0 1 1.4 1.4l-6 6a2.5 2.5 0 1 0 3.6 3.5l7.4-7.3a1 1 0 1 0-1.4-1.5l-6.7 6.7a1 1 0 0 1-1.4-1.4l6.7-6.7Z" />
          </svg>
          <span className="sr-only">Legg ved fil</span>
        </button>
        <button
          type="button"
          onClick={() => kamera.current?.click()}
          disabled={lastOpp.isPending}
          className={IKONKNAPP}
          title="Ta bilde"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4" fill="currentColor">
            <path d="M7.4 3h5.2l1.2 2H16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2.2l1.2-2ZM10 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-2a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
          </svg>
          <span className="sr-only">Ta bilde</span>
        </button>
        <input
          ref={fil}
          type="file"
          multiple
          aria-label="Velg filer å legge ved"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,application/pdf,.docx,.xlsx,.pptx,.txt"
          onChange={(e) => {
            void velgFiler(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={kamera}
          type="file"
          accept="image/*"
          capture="environment"
          aria-label="Ta et bilde"
          className="hidden"
          onChange={(e) => {
            void velgFiler(e.target.files);
            e.target.value = '';
          }}
        />
        {lastOpp.isPending ? (
          <span role="status" className="text-xs text-tekst-dempet">
            Laster opp …
          </span>
        ) : null}
        <Knapp type="submit" className="ml-auto" disabled={post.isPending || lastOpp.isPending}>
          Send
        </Knapp>
      </div>
    </form>
  );
}

// --- Detaljene -----------------------------------------------------------------

function Detaljer({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const id = useId();
  const [endrer, settEndrer] = useState(false);
  const [prioritet, settPrioritet] = useState<Prioritet | ''>(s.prioritet ?? '');
  const [etiketter, settEtiketter] = useState(s.etiketter.join(', '));
  const [ansvarlige, settAnsvarlige] = useState<string[]>(s.ansvarlige.map((a) => a.id));
  const lagre = useLagreDetaljer(sak);
  const rad =
    'grid grid-cols-[8rem_1fr] items-center gap-2 border-b border-kant px-4 py-3 last:border-b-0';

  return (
    <Seksjon
      tittel="Detaljer"
      flat
      handling={
        s.kanEndre && !endrer ? (
          <Knapp
            variant="stille"
            onClick={() => {
              settPrioritet(s.prioritet ?? '');
              settEtiketter(s.etiketter.join(', '));
              settAnsvarlige(s.ansvarlige.map((a) => a.id));
              settEndrer(true);
            }}
          >
            Endre<span className="sr-only"> detaljene</span>
          </Knapp>
        ) : undefined
      }
    >
      {endrer ? (
        <form
          aria-label="Endre detaljene"
          className="flex flex-col gap-3 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            lagre.mutate(
              {
                prioritet: prioritet === '' ? null : prioritet,
                etiketter: lesEtiketter(etiketter),
                ansvarlige,
                versjon: s.versjon,
              },
              {
                onSuccess: () => {
                  settEndrer(false);
                },
              },
            );
          }}
        >
          <div>
            <label htmlFor={`${id}-prioritet`} className="block text-sm font-medium text-tekst">
              Prioritet
            </label>
            <select
              id={`${id}-prioritet`}
              value={prioritet}
              onChange={(e) => {
                settPrioritet(PRIORITETER.find((p) => p === e.target.value) ?? '');
              }}
              className={`${FELT} mt-1 w-full`}
            >
              <option value="">Ikke satt</option>
              {PRIORITETER.map((p) => (
                <option key={p} value={p}>
                  {PRIORITETSNAVN[p]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${id}-etiketter`} className="block text-sm font-medium text-tekst">
              Etiketter <span className="font-normal text-tekst-dempet">(skilt med komma)</span>
            </label>
            <input
              id={`${id}-etiketter`}
              value={etiketter}
              onChange={(e) => {
                settEtiketter(e.target.value);
              }}
              className={`${FELT} mt-1 w-full`}
            />
          </div>
          <fieldset>
            <legend className="text-sm font-medium text-tekst">Ansvarlige</legend>
            <ul className="mt-1 max-h-48 overflow-y-auto rounded-md border border-kant">
              {s.personer.map((p) => (
                <li key={p.id}>
                  <label className="flex min-h-10 items-center gap-2 px-3 text-sm text-tekst hover:bg-flate-dempet">
                    <input
                      type="checkbox"
                      checked={ansvarlige.includes(p.id)}
                      onChange={(e) => {
                        settAnsvarlige(
                          e.target.checked
                            ? [...ansvarlige, p.id]
                            : ansvarlige.filter((x) => x !== p.id),
                        );
                      }}
                    />
                    {p.navn}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {lagre.isError ? <Feilmelding feil={lagre.error} /> : null}
          <div className="flex gap-2">
            <Knapp type="submit" disabled={lagre.isPending}>
              Lagre
            </Knapp>
            <Knapp
              variant="sekundar"
              onClick={() => {
                settEndrer(false);
              }}
            >
              Avbryt
            </Knapp>
          </div>
        </form>
      ) : (
        <dl className="text-sm">
          <div className={rad}>
            <dt className="text-tekst-dempet">Saks-ID</dt>
            <dd className="font-medium text-tekst">{s.nummer}</dd>
          </div>
          <div className={rad}>
            <dt className="text-tekst-dempet">Etiketter</dt>
            <dd className="flex flex-wrap gap-1">
              {s.etiketter.length === 0 ? (
                <span className="text-tekst-dempet">–</span>
              ) : (
                s.etiketter.map((e) => (
                  <span key={e} className={`${MERKE} bg-merke-svak text-merke-sterk`}>
                    {e}
                  </span>
                ))
              )}
            </dd>
          </div>
          {s.lenke === null ? null : (
            <div className={rad}>
              <dt className="text-tekst-dempet">Hører til</dt>
              <dd>
                <Link to={s.lenke} className="text-merke underline">
                  {HORER_TIL[s.sakstype]}
                </Link>
              </dd>
            </div>
          )}
          <div className={rad}>
            <dt className="text-tekst-dempet">Status</dt>
            <dd>
              <span className={`${MERKE} ${TONE[s.status.tone]}`}>{s.status.navn}</span>
            </dd>
          </div>
          <div className={rad}>
            <dt className="text-tekst-dempet">Prioritet</dt>
            <dd>
              {s.prioritet === null ? (
                <span className="text-tekst-dempet">Ikke satt</span>
              ) : (
                <span className={`${MERKE} ${PRIORITETSTONE[s.prioritet]}`}>
                  {PRIORITETSNAVN[s.prioritet]}
                </span>
              )}
            </dd>
          </div>
          <div className={rad}>
            <dt className="text-tekst-dempet">Ansvarlige</dt>
            <dd className="flex items-center">
              {s.ansvarlige.length === 0 ? (
                <span className="text-tekst-dempet">Ingen</span>
              ) : (
                <>
                  <span className="flex -space-x-2">
                    {s.ansvarlige.map((a) => (
                      <Avatar key={a.id} person={a} liten />
                    ))}
                  </span>
                  <span className="sr-only">{s.ansvarlige.map((a) => a.navn).join(', ')}</span>
                  {s.ansvarlige.length === 1 ? (
                    <span className="ml-2 text-tekst">{s.ansvarlige[0]?.navn}</span>
                  ) : null}
                </>
              )}
            </dd>
          </div>
        </dl>
      )}
    </Seksjon>
  );
}

// --- Deloppgavene --------------------------------------------------------------

function Deloppgaver({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const id = useId();
  const [ny, settNy] = useState<string | null>(null);
  const opprett = useNyDeloppgave(sak);
  const endre = useEndreDeloppgave();
  const slett = useSlettDeloppgave();
  return (
    <Seksjon
      tittel="Deloppgaver"
      flat
      handling={
        s.kanEndre && ny === null ? (
          <Knapp
            variant="stille"
            onClick={() => {
              settNy('');
              window.requestAnimationFrame(() => {
                document.getElementById(`${id}-ny`)?.focus();
              });
            }}
          >
            + Deloppgave
          </Knapp>
        ) : undefined
      }
    >
      {s.deloppgaver.length === 0 && ny === null ? (
        <p className="p-4 text-sm text-tekst-dempet">Ingen deloppgaver.</p>
      ) : (
        <ul className="m-3 rounded-md border border-kant">
          {s.deloppgaver.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-2 border-b border-kant px-3 last:border-b-0"
            >
              <label className="flex min-h-11 flex-1 items-center gap-2 text-sm font-medium text-tekst">
                <input
                  type="checkbox"
                  checked={d.ferdig}
                  disabled={!s.kanUtfore}
                  onChange={(e) => {
                    endre.mutate({ deloppgaveId: d.id, tekst: d.tekst, ferdig: e.target.checked });
                  }}
                  className="accent-[var(--positiv)]"
                />
                <span className={d.ferdig ? 'text-tekst-dempet line-through' : ''}>{d.tekst}</span>
              </label>
              {s.kanEndre ? (
                <button
                  type="button"
                  onClick={() => {
                    slett.mutate(d.id);
                  }}
                  className={IKONKNAPP}
                >
                  <span aria-hidden="true">×</span>
                  <span className="sr-only">Slett deloppgaven {d.tekst}</span>
                </button>
              ) : null}
            </li>
          ))}
          {ny !== null ? (
            <li className="p-2">
              <form
                aria-label="Ny deloppgave"
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (ny.trim() === '') return;
                  opprett.mutate(ny.trim(), {
                    onSuccess: () => {
                      settNy(null);
                    },
                  });
                }}
              >
                <label htmlFor={`${id}-ny`} className="sr-only">
                  Ny deloppgave
                </label>
                <input
                  id={`${id}-ny`}
                  value={ny}
                  maxLength={200}
                  onChange={(e) => {
                    settNy(e.target.value);
                  }}
                  className={`${FELT} flex-1`}
                />
                <Knapp type="submit" disabled={opprett.isPending}>
                  Legg til
                </Knapp>
              </form>
            </li>
          ) : null}
        </ul>
      )}
    </Seksjon>
  );
}

// --- Vedleggene ----------------------------------------------------------------

function Vedlegg({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const fil = useRef<HTMLInputElement>(null);
  const lastOpp = useLastOppVedlegg(sak);
  const slett = useSlettVedlegg();
  const [feil, settFeil] = useState<string | null>(null);
  return (
    <Seksjon
      tittel="Vedlegg"
      flat
      handling={
        s.kanBidra ? (
          <>
            <Knapp
              variant="stille"
              onClick={() => fil.current?.click()}
              disabled={lastOpp.isPending}
            >
              {lastOpp.isPending ? 'Laster opp …' : '+ Vedlegg'}
            </Knapp>
            <input
              ref={fil}
              type="file"
              aria-label="Velg en fil å legge ved saken"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,application/pdf,.docx,.xlsx,.pptx,.txt"
              onChange={(e) => {
                const valgt = e.target.files?.[0];
                e.target.value = '';
                if (valgt === undefined) return;
                settFeil(null);
                void krympBilde(valgt)
                  .catch(() => valgt)
                  .then((klar) => {
                    if (klar.size > MAKS_VEDLEGG) {
                      settFeil(`${valgt.name} er større enn 10 MB.`);
                      return;
                    }
                    lastOpp.mutate(klar);
                  });
              }}
            />
          </>
        ) : undefined
      }
    >
      {feil ? (
        <p role="alert" className="px-4 pt-3 text-sm text-negativ">
          {feil}
        </p>
      ) : null}
      {lastOpp.isError ? (
        <div className="px-4 pt-3">
          <Feilmelding feil={lastOpp.error} />
        </div>
      ) : null}
      {s.vedlegg.length === 0 ? (
        <p className="p-4 text-sm text-tekst-dempet">Ingen vedlegg.</p>
      ) : (
        <ul>
          {s.vedlegg.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-3 border-b border-kant px-4 py-2 last:border-b-0"
            >
              <Filikon mimetype={v.mimetype} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-tekst">{v.filnavn}</p>
                <p className="text-xs text-tekst-dempet">
                  {filstorrelse(v.storrelse)}
                  {v.lastetOpp.av === null ? '' : ` · ${v.lastetOpp.av}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void lastNed(v.id, v.filnavn)}
                className={`${IKONKNAPP} bg-merke-svak text-merke-sterk`}
              >
                <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4" fill="currentColor">
                  <path d="M10 3a1 1 0 0 1 1 1v7.6l2.3-2.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.4L9 11.6V4a1 1 0 0 1 1-1ZM4 15a1 1 0 0 1 1 1h10a1 1 0 1 1 2 0 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 1 1 0 0 1 1-1Z" />
                </svg>
                <span className="sr-only">Last ned {v.filnavn}</span>
              </button>
              {v.kanSlette ? (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Slette ${v.filnavn}?`)) slett.mutate(v.id);
                  }}
                  className={`${IKONKNAPP} bg-negativ-svak text-negativ`}
                >
                  <svg
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                    className="size-4"
                    fill="currentColor"
                  >
                    <path d="M8 2h4a1 1 0 0 1 1 1v1h3a1 1 0 1 1 0 2h-1v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6H4a1 1 0 0 1 0-2h3V3a1 1 0 0 1 1-1Zm1 2h2V4H9v0Zm-2 2v10h6V6H7Z" />
                  </svg>
                  <span className="sr-only">Slett {v.filnavn}</span>
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Seksjon>
  );
}

// --- Tiden ---------------------------------------------------------------------

function Tid({ s, sak }: { readonly s: SakDto; readonly sak: Sakreferanse }): ReactElement {
  const id = useId();
  const [forer, settForer] = useState(false);
  const [dato, settDato] = useState(datoIOslo(new Date()));
  const [varighet, settVarighet] = useState('');
  const [kommentar, settKommentar] = useState('');
  const [feil, settFeil] = useState<string | null>(null);
  const forTid = useForTid(sak);
  const slett = useSlettTid();
  return (
    <Seksjon
      tittel={`Tid${s.tid.totalMinutter === 0 ? '' : ` · ${tidstekst(s.tid.totalMinutter)}`}`}
      flat
      handling={
        s.kanUtfore && !forer ? (
          <Knapp
            variant="stille"
            onClick={() => {
              settForer(true);
            }}
          >
            + Før tid
          </Knapp>
        ) : undefined
      }
    >
      {forer ? (
        <form
          aria-label="Før tid"
          noValidate
          className="flex flex-col gap-3 border-b border-kant p-4"
          onSubmit={(e) => {
            e.preventDefault();
            const minutter = lesVarighet(varighet);
            if (minutter === null || minutter < 1 || minutter > 1440) {
              settFeil('Skriv tiden som 1:30, 1,5 eller 90 minutter.');
              return;
            }
            settFeil(null);
            forTid.mutate(
              { dato, minutter, kommentar: kommentar.trim() === '' ? null : kommentar.trim() },
              {
                onSuccess: () => {
                  settForer(false);
                  settVarighet('');
                  settKommentar('');
                },
              },
            );
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`${id}-dato`} className="block text-sm font-medium text-tekst">
                Dato
              </label>
              <input
                id={`${id}-dato`}
                type="date"
                value={dato}
                onChange={(e) => {
                  settDato(e.target.value);
                }}
                className={`${FELT} mt-1 w-full`}
              />
            </div>
            <div>
              <label htmlFor={`${id}-tid`} className="block text-sm font-medium text-tekst">
                Tid (t:mm)
              </label>
              <input
                id={`${id}-tid`}
                value={varighet}
                inputMode="decimal"
                onChange={(e) => {
                  settVarighet(e.target.value);
                }}
                className={`${FELT} mt-1 w-full`}
              />
            </div>
          </div>
          <div>
            <label htmlFor={`${id}-kommentar`} className="block text-sm font-medium text-tekst">
              Hva ble gjort <span className="font-normal text-tekst-dempet">(valgfri)</span>
            </label>
            <input
              id={`${id}-kommentar`}
              value={kommentar}
              maxLength={200}
              onChange={(e) => {
                settKommentar(e.target.value);
              }}
              className={`${FELT} mt-1 w-full`}
            />
          </div>
          {feil ? (
            <p role="alert" className="text-sm text-negativ">
              {feil}
            </p>
          ) : null}
          {forTid.isError ? <Feilmelding feil={forTid.error} /> : null}
          <div className="flex gap-2">
            <Knapp type="submit" disabled={forTid.isPending}>
              Lagre
            </Knapp>
            <Knapp
              variant="sekundar"
              onClick={() => {
                settForer(false);
              }}
            >
              Avbryt
            </Knapp>
          </div>
        </form>
      ) : null}
      {s.tid.foringer.length === 0 ? (
        forer ? null : (
          <p className="p-4 text-sm text-tekst-dempet">Ingen tid er ført.</p>
        )
      ) : (
        <ul>
          {s.tid.foringer.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-3 border-b border-kant px-4 py-2 last:border-b-0"
            >
              <Avatar person={t.person} liten />
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-tekst">
                  <span className="font-medium">{tidstekst(t.minutter)}</span>{' '}
                  <span className="text-tekst-dempet">· {datotekst(t.dato)}</span>
                </p>
                {t.kommentar === null ? null : (
                  <p className="truncate text-xs text-tekst-dempet">{t.kommentar}</p>
                )}
              </div>
              {t.kanSlette ? (
                <button
                  type="button"
                  onClick={() => {
                    slett.mutate(t.id);
                  }}
                  className={IKONKNAPP}
                >
                  <span aria-hidden="true">×</span>
                  <span className="sr-only">Slett tidsføringen {t.dato}</span>
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Seksjon>
  );
}
